import {StageManager} from "../StageMaganer/StageManager";
import {PublicTicketsFormDefs} from "../PublicTicketsForm";
import {
    projectPurchasedDates,
    resolveContinuousDateRange,
    resolveContinuousDateRangeClick,
    sortEventDatesBySequence
} from "../continuousDateRange";

class GlobalState {
    /** @var {PublicTicketsFormDefs.Stage[]} */
    stageArray;
    /** @var {StageManager} */
    stageManager;
    /** @var {PublicTicketsFormDefs.Stage} */
    currentStage;

    /** @var {PublicEventResource.PublicEventResource|undefined} */
    parentEvent;

    /** @var {PublicEventResource.PublicEventResource|undefined} */
    selectedDate

    /** @var {PublicEventResource.PublicEventResource[]} */
    selectedDates

    /** @var {string} */
    dateRangeMode;

    /** @var {PublicEventResource.SeatType} */
    selectedSeatType

    /** @var {boolean} */
    generalAdmissionEnabled

    /** @var {string} */
    effectiveReservedEntryLabel

    /** @var {PublicTicketsFormDefs.ReservedSeatSelection[]} */
    selectedSeats

    /** @var {PublicTicketsFormDefs.SectionTicketCount[]} */
    generalAdmissionTicketCounts
    /** @var {PublicTicketsFormDefs.SectionTicketCount[]} */
    reserveTicketCounts

    /** @var {string} */
    discountCode

    /** @var {PublicTicketsFormDefs.Addon[]} */
    addons

    /** @var {PublicTicketsFormDefs.PaymentInfo} */
    paymentInfo

    /** @var {string} */
    howDidYouFindUs

    /** @var {string} */
    customerSpecialRequests

    /** @var {boolean} */
    isCurrentEventView;

    /** @var {PublicTicketsFormDefs.Stage[]} */
    hiddenStages;

    /** @var {string} */
    baseRoute;

    /** @var {string} */
    adTrackingKeyword;

    /** @var {string} */
    adTrackingSessionPublicId;

    /** @var {boolean} */
    isUsingStripePaymentElements;

    /** @var {boolean} */
    recoveringFromCapacityConflict;

    /** @var {boolean} */
    restoredPurchasedDatesAuthoritative;

    /** @var {PublicTicketsFormDefs.PublicTicketConfig} */
    publicTicketConfig;

    constructor() {
        // Initial state
        this.stageArray = [
            'show-select',
            'date-select',
            'seat-select',
            'seat-type-select',
            'payment-info',
            'summary',
        ];

        this.selectedDate = undefined;
        this.selectedDates = [];
        this.dateRangeMode = 'inclusive';
        this.selectedSeatType = undefined;
        this.generalAdmissionEnabled = false;
        this.effectiveReservedEntryLabel = '';
        this.selectedSeats = [];
        this.generalAdmissionTicketCounts = [];
        this.reserveTicketCounts = [];
        this.discountCode = '';
        this.addons = [];

        this.howDidYouFindUs = '';
        this.customerSpecialRequests = '';

        this.currentStage = 'show-select';

        this.isCurrentEventView = false;

        this.hiddenStages = [];

        this.baseRoute = '';

        this.adTrackingKeyword = '';
        this.adTrackingSessionPublicId = '';

        this.isUsingStripePaymentElements = false;
        this.recoveringFromCapacityConflict = false;
        this.restoredPurchasedDatesAuthoritative = false;
        this.stripeInstance = undefined;
        this.publicTicketConfig = {
            multimapTutorialShow: false,
            rvStallUpsell: this.getDisabledRvStallUpsellConfig(),
        };
        // Stage Manager
        this.stageManager = this.getInitialStageManager();
    }

    isExpressMode() {
        // Extended by child class
        return false;
    }

    /**
     * @return {boolean}
     */
    isMixedOrderAuthoringEnabled() {
        return this.parentEvent?.mixedOrderAuthoringEnabled === true;
    }

    /**
     * Composition activation is supplied by admin state so inherited stages do not infer update support.
     *
     * @return {boolean}
     */
    isCompositionEnabled() {
        if (this.isMixedOrderAuthoringEnabled() !== true || this.isExpressMode()) {
            return false;
        }

        if (typeof this.isAdminCompositionEnabled === 'function') {
            return this.isAdminCompositionEnabled();
        }

        return typeof this.isOrderUpdate !== 'boolean';
    }

    /**
     * Public composition is intentionally opt-in. Admin state subclasses expose isOrderUpdate.
     *
     * @return {boolean}
     */
    isPublicMixedCompositionEnabled() {
        return this.isCompositionEnabled();
    }

    /**
     * @return {boolean}
     */
    hasGeneralAdmissionCapability() {
        const purchasedDates = this.getPurchasedDates();
        const datesWithCapability = purchasedDates.filter(eventDate => {
            return typeof eventDate?.hasGeneralAdmissionTicketTypesAvailable === 'boolean';
        });

        if (datesWithCapability.length > 0) {
            return datesWithCapability.every(eventDate => eventDate.hasGeneralAdmissionTicketTypesAvailable === true);
        }

        return this.parentEvent?.priceData?.generalAdmission?.isAvailable === true;
    }

    /**
     * @return {boolean}
     */
    hasReservedCapability() {
        const purchasedDates = this.getPurchasedDates();
        const datesWithCapability = purchasedDates.filter(eventDate => {
            return typeof eventDate?.hasReservedTicketTypesAvailable === 'boolean';
        });

        if (datesWithCapability.length > 0) {
            return datesWithCapability.every(eventDate => eventDate.hasReservedTicketTypesAvailable === true);
        }

        return this.parentEvent?.priceData?.reservedSection?.isAvailable === true;
    }

    /**
     * @param {PublicTicketsFormDefs.SectionTicketCount[]} counts
     * @return {boolean}
     */
    hasPositiveTicketCounts(counts) {
        return (Array.isArray(counts) ? counts : []).some(ticket => Number(ticket?.count) > 0);
    }

    hasPositiveGeneralAdmissionTicketCounts() {
        return this.hasPositiveTicketCounts(this.generalAdmissionTicketCounts);
    }

    getInitialStageManager() {
        return new StageManager(this);
    }

    hasRecordChanged(originalRecord, newRecord, fieldToCheck = 'id') {
        // If either of them is null but the other isn't, we know it has changed
        if (!originalRecord && newRecord) {
            return true;
        }
        if (originalRecord && !newRecord) {
            return true;
        }

        // If both records are equal, then either both are empty and nothing changed, or they are
        // strict match which means neither has changed too.
        if (originalRecord === newRecord) {
            return false;
        }

        // Finally, both must be defined and not a strict match, compare by given field
        return !(originalRecord[fieldToCheck] === newRecord[fieldToCheck]);
    }

    /**
     * @param {PublicEventResource.PublicEventResource} parentEvent
     */
    setParentEvent(parentEvent) {
        const previousMixedFlow = this.isPublicMixedCompositionEnabled();

        // Check if we selected a different parent event
        const recordChanged = this.hasRecordChanged(
            this.parentEvent,
            parentEvent
        );

        // Update in memory
        this.parentEvent = parentEvent
        this.dateRangeMode = parentEvent?.dateRangeMode || 'inclusive';

        // Clear next stage if required
        if (recordChanged) {
            this.stageManager.clearStageInstance('date-select');

            this.selectedDate = undefined;
            this.selectedDates = [];
            this.customerSpecialRequests = '';
            const nextMixedFlow = this.isPublicMixedCompositionEnabled();
            this.clearStateAfterDateSelectionChange(
                previousMixedFlow || nextMixedFlow,
                !this.isOrderUpdate && (previousMixedFlow || nextMixedFlow)
            );
        }
    };


    /**
     * @param {PublicEventResource.PublicEventResource} eventDate
     */
    setEventDate(eventDate) {
        const selectedDates = eventDate
            ? [eventDate]
            : [];

        this.setSelectedDates(selectedDates);
    }

    /**
     * @param {PublicEventResource.PublicEventResource[]} eventDates
     */
    setSelectedDates(eventDates) {
        const normalizedDates = this.normalizeSelectedDates(eventDates);

        // Check if we selected a different event date
        const recordChanged = this.hasSelectedDateSetChanged(normalizedDates);

        // Update in memory
        this.selectedDates = normalizedDates;
        this.selectedDate = normalizedDates.length
            ? normalizedDates[0]
            : undefined;

        // Clear next stage if required
        if (recordChanged) {
            this.clearStateAfterDateSelectionChange(this.isPublicMixedCompositionEnabled());
        }
    }

    /**
     * @param {PublicEventResource.PublicEventResource[]} eventDates
     * @return {PublicEventResource.PublicEventResource[]}
     */
    normalizeSelectedDates(eventDates) {
        const uniqueDatesById = new Map();

        if (Array.isArray(eventDates) === false) {
            return [];
        }

        eventDates.forEach(eventDate => {
            if (eventDate && eventDate.id && uniqueDatesById.has(eventDate.id) === false) {
                uniqueDatesById.set(eventDate.id, eventDate);
            }
        });

        return sortEventDatesBySequence(Array.from(uniqueDatesById.values()));
    }

    /**
     * @param {PublicEventResource.PublicEventResource[]} nextSelectedDates
     * @return {boolean}
     */
    hasSelectedDateSetChanged(nextSelectedDates) {
        if (this.selectedDates.length !== nextSelectedDates.length) {
            return true;
        }

        return this.selectedDates.some((selectedDate, index) => {
            return selectedDate.id !== nextSelectedDates[index].id;
        });
    }

    clearStateAfterDateSelectionChange(forceMixedFlow = false, clearOrderId = false) {
        this.selectedSeatType = undefined;
        this.generalAdmissionEnabled = false;
        this.effectiveReservedEntryLabel = '';
        this.selectedSeats = [];
        this.generalAdmissionTicketCounts = [];
        this.reserveTicketCounts = [];
        this.addons = [];
        this.paymentInfo = undefined;

        if (forceMixedFlow || this.isPublicMixedCompositionEnabled()) {
            this.discountCode = '';
        }

        if (clearOrderId) {
            this.orderId = undefined;
        }

        this.stageManager.clearStageInstance('seat-select');
        this.stageManager.clearStageInstance('seat-type-select');
        this.stageManager.clearStageInstance('payment-info');
        this.stageManager.clearStageInstance('summary');
    }

    isMultipleDatesSelectionMode() {
        return this.parentEvent?.dateSelectionType === 'multiple_dates';
    }

    isContinuousDateRangeMode() {
        return this.isMultipleDatesSelectionMode() && this.parentEvent?.requireContinuousDateRange === true;
    }

    normalizeContinuousRangeFromSelectedDates(eligibleDates = undefined) {
        if (!this.isContinuousDateRangeMode() || this.selectedDates.length < 2) {
            return false;
        }

        const dates = eligibleDates || [
            ...(this.parentEvent?.pendingDates || []),
            ...this.selectedDates,
        ];
        const canonicalDates = resolveContinuousDateRange(
            dates,
            this.selectedDates[0],
            this.selectedDates[this.selectedDates.length - 1]
        );

        if (canonicalDates.length === 0 || !this.hasSelectedDateSetChanged(canonicalDates)) {
            return false;
        }

        this.setSelectedDates(canonicalDates);

        return true;
    }

    setContinuousRangeDate(eventDate, eligibleDates = undefined) {
        const result = resolveContinuousDateRangeClick(
            eligibleDates || this.parentEvent?.pendingDates || [],
            this.selectedDates,
            eventDate
        );

        if (result.valid) {
            this.setSelectedDates(result.dates);
        }

        return result;
    }

    /**
     * @return {PublicEventResource.PublicEventResource[]}
     */
    getSelectedChildDates(purchasedDates = this.getPurchasedDates()) {
        if (purchasedDates.length === 0) {
            return [];
        }

        return purchasedDates.slice(1);
    }

    getSubOrderPayload(purchasedDates = this.getPurchasedDates()) {
        return this.getSelectedChildDates(purchasedDates).map(eventDate => {
            return {
                event_date_id: eventDate.id,
            };
        });
    }

    getPurchasedDates() {
        if (this.restoredPurchasedDatesAuthoritative) {
            return this.selectedDates;
        }

        return projectPurchasedDates(this.selectedDates, this.dateRangeMode);
    }

    markRestoredPurchasedDatesAsLiveSelection() {
        this.restoredPurchasedDatesAuthoritative = false;
    }

    /**
     * @param {PublicEventResource.SeatType} seatType
     */
    setSeatType(seatType) {
        // Check if we selected a different seat type
        const recordChanged = this.hasRecordChanged(
            this.selectedSeatType,
            seatType
        );

        // Update in memory
        // noinspection JSValidateTypes
        this.selectedSeatType = seatType;

        // Clear next stage if required
        if (recordChanged) {
            // Clear next stage if seat was changed
            this.stageManager.clearStageInstance('seat-select');
            this.stageManager.clearStageInstance('seat-type-select');
            this.stageManager.clearStageInstance('summary');
        }
    }

    /**
     * @param {boolean} enabled
     */
    setGeneralAdmissionEnabled(enabled) {
        this.generalAdmissionEnabled = enabled === true;
    }

    /**
     * Save the order id generated from the server
     *
     * @param {string} orderId
     */
    setOrderId(orderId) {
        this.orderId = orderId;
    }

    /**
     * @param {PublicTicketsFormDefs.ReservedSeatSelection[]} seats
     */
    setSelectedSeats(seats) {
        this.selectedSeats = Array.isArray(seats) ? seats : [];

        if (this.isPublicMixedCompositionEnabled()) {
            const selectedGroupIds = new Set(
                this.selectedSeats
                    .map(seat => seat?.seat_group_id)
                    .filter(groupId => !!groupId)
            );

            this.reserveTicketCounts = selectedGroupIds.size === 0
                ? []
                : this.reserveTicketCounts.filter(ticket => selectedGroupIds.has(ticket.groupId));
        }

        // TODO: check if seats were truly changed

        this.stageManager.clearStageInstance('seat-type-select');
    }

    /**
     * @param {PublicTicketsFormDefs.SectionTicketCount[]} tickets
     */
    setGeneralAdmissionTicketCounts(tickets) {
        this.generalAdmissionTicketCounts = tickets;

        // TODO: Check if ticket counts were truly changed

        this.stageManager.clearStageInstance('payment-info');
        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {PublicTicketsFormDefs.SectionTicketCount[]} tickets
     */
    setReserveTicketCounts(tickets) {
        this.reserveTicketCounts = tickets;

        // TODO: Check if ticket counts were truly changed

        this.stageManager.clearStageInstance('payment-info');
        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {PublicTicketsFormDefs.Addon[]} addons
     */
    setAddons(addons) {
        this.addons = addons;

        // TODO: Check if addons were truly changed

        this.stageManager.clearStageInstance('payment-info');
        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {string} discountCode
     */
    setDiscountCode(discountCode) {
        this.discountCode = discountCode;

        // TODO: Check if discount code was truly changed

        // Clear payment and summary stage, unless we are in payment info stage
        // then clear only summary
        if (this.stageManager.globalState.currentStage !== 'payment-info') {
            this.stageManager.clearStageInstance('payment-info');
        }
        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {PublicTicketsFormDefs.PaymentInfo} paymentInfo
     */
    setPaymentInfo(paymentInfo) {
        this.paymentInfo = paymentInfo;

        // TODO: Check if payment info was truly changed

        this.stageManager.clearStageInstance('summary');
    }

    /**
     * @param {string} howDidYouFindUs
     */
    setHowDidYouFindUs(howDidYouFindUs) {
        this.howDidYouFindUs = howDidYouFindUs;
    }

    /**
     * @param {string} customerSpecialRequests
     */
    setCustomerSpecialRequests(customerSpecialRequests) {
        this.customerSpecialRequests = customerSpecialRequests;
    }

    /**
     * @param {boolean} isCurrentEventView
     */
    setIsCurrentEventView(isCurrentEventView) {
        this.isCurrentEventView = isCurrentEventView;
    }

    /**
     * @param {PublicTicketsFormDefs.Stage[]} hiddenStages
     */
    setHiddenStages(hiddenStages) {
        const normalizedHiddenStages = Array.isArray(hiddenStages)
            ? hiddenStages
            : [];

        this.hiddenStages = normalizedHiddenStages.filter(stage => this.stageArray.includes(stage));
    }

    /**
     * @param {Partial<PublicTicketsFormDefs.PublicTicketConfig>} config
     */
    setPublicTicketConfig(config) {
        this.publicTicketConfig = {
            multimapTutorialShow: config?.multimapTutorialShow === true,
            rvStallUpsell: this.normalizeRvStallUpsellConfig(config?.rvStallUpsell),
        };
    }

    getDisabledRvStallUpsellConfig() {
        return {
            enabled: false,
            parentMultiMapPublicId: '',
            rvChildMapPublicId: '',
            stallsChildMapPublicId: '',
        };
    }

    normalizeRvStallUpsellConfig(config) {
        if (!config || typeof config !== 'object') {
            return this.getDisabledRvStallUpsellConfig();
        }

        const parentMultiMapPublicId = this.normalizePublicIdConfigValue(config.parentMultiMapPublicId);
        const rvChildMapPublicId = this.normalizePublicIdConfigValue(config.rvChildMapPublicId);
        const stallsChildMapPublicId = this.normalizePublicIdConfigValue(config.stallsChildMapPublicId);

        if (config.enabled !== true || !parentMultiMapPublicId || !rvChildMapPublicId || !stallsChildMapPublicId) {
            return this.getDisabledRvStallUpsellConfig();
        }

        return {
            enabled: true,
            parentMultiMapPublicId,
            rvChildMapPublicId,
            stallsChildMapPublicId,
        };
    }

    normalizePublicIdConfigValue(value) {
        return typeof value === 'string'
            ? value.trim()
            : '';
    }

    /**
     * @param {string} adTrackingKeyword
     * @param {string} adTrackingSessionPublicId
     */
    setAdTrackingContext(adTrackingKeyword, adTrackingSessionPublicId) {
        this.adTrackingKeyword = String(adTrackingKeyword || '').trim().toLowerCase();
        this.adTrackingSessionPublicId = String(adTrackingSessionPublicId || '').trim();

        // Keyword is authoritative for linking with orders.
        if (!this.adTrackingKeyword) {
            this.adTrackingSessionPublicId = '';
        }
    }

    /**
     * @param {string} trackingCode
     */
    setManualTrackingCode(trackingCode) {
        this.adTrackingKeyword = String(trackingCode || '').trim().toLowerCase();
        this.adTrackingSessionPublicId = '';
    }

    /**
     * @param {PublicTicketsFormDefs.Stage} stage
     */
    isStageHidden(stage) {
        return this.hiddenStages.includes(stage);
    }

    /**
     * @param {boolean} isUsingStripePaymentElements
     */
    setIsUsingStripePaymentElements(isUsingStripePaymentElements) {
        this.isUsingStripePaymentElements = isUsingStripePaymentElements;
    }

    setStripeInstance(stripeInstance) {
        this.stripeInstance = stripeInstance;
    }

    /**
     * Returns order data in a format the server can understand
     */
    getOrderDataForServer(useRealPaymentData = false) {
        // Build reserved seats array
        let reserved_seats = [];

        if (this.selectedSeats.length) {
            reserved_seats = this.selectedSeats;
        }


        // Build seats array
        const seats = [];

        this.generalAdmissionTicketCounts.forEach(seatCount => {
            seats.push({
                seat_group_id: seatCount.groupId,
                visitor_type: seatCount.visitorTypeId,
                quantity: seatCount.count,
            });
        })
        this.reserveTicketCounts.forEach(seatCount => {
            seats.push({
                seat_group_id: seatCount.groupId,
                visitor_type: seatCount.visitorTypeId,
                quantity: seatCount.count,
            });
        })

        // Build addons array
        const addons = this.addons.map(addon => {
            return {
                id: addon.id,
                key: addon.key,
                quantity: (addon.count === undefined) ? 1 : addon.count,
                frozen: addon.frozen,
            };
        })

        let paymentInfo = this.paymentInfo;

        // Set confirmation token
        if (paymentInfo && paymentInfo.stripe_confirmation_token) {
            paymentInfo.confirmation_token = paymentInfo.stripe_confirmation_token;
        }

        const discountsArray = this.discountCode ? [{code: this.discountCode}] : [];

        const purchasedDates = this.getPurchasedDates();

        const data = {
            order_id: this.orderId,
            event_id: this.parentEvent.id,
            event_date_id: purchasedDates[0]?.id,
            sub_orders: this.getSubOrderPayload(purchasedDates),

            seats,
            addons,
            reserved_seats,

            ...paymentInfo,
            how_did_you_find_us: this.howDidYouFindUs,
            discounts: discountsArray,
        };

        if (this.adTrackingKeyword) {
            data.ad_tracking_keyword = this.adTrackingKeyword;
            data.ad_tracking_session_public_id = this.adTrackingSessionPublicId;
        }

        if (this.isPublicMixedCompositionEnabled()) {
            data.mixed_order_composition = true;
        }

        // Customer requests are a public-only, opted-in booking field.
        if (this.parentEvent?.customerSpecialRequestsEnabled === true && typeof this.isOrderUpdate !== 'boolean') {
            data.customer_special_requests = this.customerSpecialRequests;
        }

        return data;
    }
}

export {GlobalState};
